import React, { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import { Typography } from 'antd';

import { useCurrentUser } from '../../models/useCurrentUser';
import { useUser } from '../../models/useUser';

const { Text } = Typography;

const DISMISSED_FIELD = 'homeScreenPromptDismissedAt';

/** 'ios' | 'android' | null — the card is for phones only. */
export function phonePlatform(ua) {
  const s = String(ua || '');
  if (/iPhone|iPad|iPod/i.test(s)) return 'ios';
  if (/Android/i.test(s)) return 'android';
  return null;
}

/** Already opened from a home-screen icon: nothing to ask. */
export function isStandalone(win) {
  try {
    if (win.navigator && win.navigator.standalone) return true;
    return !!(win.matchMedia && win.matchMedia('(display-mode: standalone)').matches);
  } catch (_) {
    return false;
  }
}

/**
 * Offer only on a phone browser (not an installed app), to a signed-in person
 * who has not already said no.
 */
export function shouldOfferHomeScreen({ authUser, firestoreUser, platform, standalone }) {
  if (!authUser || authUser.isAnonymous) return false;
  if (!platform || standalone) return false;
  if (firestoreUser && firestoreUser[DISMISSED_FIELD]) return false;
  return true;
}

const primary = {
  width: '100%', height: 48, fontSize: 16, fontWeight: 600, borderRadius: 8,
  cursor: 'pointer', background: '#ff0098', border: '1px solid #ff0098', color: '#fff', marginBottom: 8,
};

const ShareGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: '-3px' }}>
    <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
  </svg>
);

function Steps({ platform, canInstall }) {
  if (platform === 'ios') {
    return (
      <ol style={{ margin: '0 0 12px', paddingLeft: 24, fontSize: 16, color: '#262626', lineHeight: 1.6, listStyle: 'decimal' }}>
        <li>Tap <strong>Share</strong> <ShareGlyph /> at the bottom of Safari.</li>
        <li>Choose <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
      </ol>
    );
  }
  if (canInstall) return null;
  return (
    <ol style={{ margin: '0 0 12px', paddingLeft: 24, fontSize: 16, color: '#262626', lineHeight: 1.6, listStyle: 'decimal' }}>
      <li>Tap the <strong>⋮</strong> menu at the top of Chrome.</li>
      <li>Choose <strong>Add to Home screen</strong>, then <strong>Install</strong>.</li>
    </ol>
  );
}

/**
 * "Add Striketip to your home screen": the way back to the app in one tap.
 * Android Chrome gets a real Install button (beforeinstallprompt, captured in
 * index.js); iPhone gets the two Safari steps. Shown once after a join or a
 * claim and on the dashboard; "Not now" hides it for good; hidden outright
 * when the site is already running from a home-screen icon.
 */
export default function HomeScreenCard() {
  const [, authUser] = useCurrentUser();
  const uid = authUser && !authUser.isAnonymous ? authUser.uid : null;
  const [userLoading, firestoreUser] = useUser(uid);
  const [hidden, setHidden] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(() => !!window.__strikeInstallPrompt);

  useEffect(() => {
    const onReady = () => setCanInstall(true);
    const onInstalled = () => setInstalled(true);
    window.addEventListener('strike-install-ready', onReady);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('strike-install-ready', onReady);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const platform = phonePlatform(window.navigator && window.navigator.userAgent);
  if (hidden || userLoading
    || !shouldOfferHomeScreen({ authUser, firestoreUser, platform, standalone: isStandalone(window) })) {
    return null;
  }

  const dismiss = async () => {
    setHidden(true);
    try {
      await firebase.firestore().collection('users').doc(uid).set(
        { [DISMISSED_FIELD]: firebase.firestore.FieldValue.serverTimestamp() },
        { merge: true },
      );
    } catch (err) {
      console.warn('[home-screen] dismiss write failed:', err && err.message);
    }
  };

  const install = async () => {
    const prompt = window.__strikeInstallPrompt;
    if (!prompt) return;
    try {
      prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice && choice.outcome === 'accepted') setInstalled(true);
    } catch (err) {
      console.warn('[home-screen] install prompt failed:', err && err.message);
    } finally {
      window.__strikeInstallPrompt = null;
      setCanInstall(false);
    }
  };

  if (installed) {
    return (
      <div className="home-screen-card" style={{ borderRadius: 12, padding: 16, background: '#f6ffed', border: '1px solid #b7eb8f', marginBottom: 16 }}>
        <Text strong style={{ fontSize: 16, display: 'block' }}>Added. Striketip is on your home screen.</Text>
      </div>
    );
  }

  return (
    <div className="home-screen-card" style={{ borderRadius: 12, padding: 16, background: '#fafafa', border: '1px solid #d9d9d9', marginBottom: 16 }}>
      <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>Add Striketip to your home screen</Text>
      <Text style={{ fontSize: 16, color: '#595959', display: 'block', marginBottom: 12 }}>
        One tap to get back to your tips, like an app.
      </Text>
      <Steps platform={platform} canInstall={platform === 'android' && canInstall} />
      {platform === 'android' && canInstall && (
        <button type="button" onClick={install} style={primary}>Add to home screen</button>
      )}
      <button
        type="button"
        onClick={dismiss}
        style={{ background: 'none', border: 'none', color: '#8c8c8c', fontSize: 16, minHeight: 44, width: '100%', cursor: 'pointer' }}
      >
        Not now
      </button>
    </div>
  );
}
